import { IncomingMessage } from 'http';
import { NextApiRequestCookies } from 'next/dist/server/api-utils';
import type { ParsedUrlQuery } from 'querystring';
import { renderToString } from 'react-dom/server';
import {
    Configure,
    ConfigureProps,
    Index,
    InstantSearch,
    InstantSearchSSRProvider,
    getServerState,
} from 'react-instantsearch';
import {
    VirtualPagination,
    VirtualSearch,
    VirtualFacets,
    VirtualSortBy,
    VirtualRefinementList,
} from '~/features/productList/components/VirtualWidgets';
import type { P10SearchPage, P40ProductListPage } from '~/lib';
import {
    algoliaClient,
    createInstantSearchRouting,
    Facets,
    generateAlgoliaConfiguration,
    proxySearchClient,
} from '~/services/instantsearch';
import {
    ALGOLIA_INDEX_ID_PRODUCT_LIST,
    ALGOLIA_INDEX_MAIN,
} from '~/services/instantsearch/variables';

type Props = {
    req: IncomingMessage & {
        cookies: NextApiRequestCookies;
    };
    page: P40ProductListPage | P10SearchPage;
    pageSize: number;
    query: ParsedUrlQuery;
};

export const generateProductListSSR = async ({ req, page, pageSize, query }: Props) => {
    const { productids, productIds, campaign } = query;

    let configuration: ConfigureProps;

    if (page.type === 'p10SearchPage') {
        configuration = generateAlgoliaConfiguration({
            filter: 'search',
            filterType: 'Context Rule Name',
        });
    } else {
        const { dataConfiguration, dataType, parentDataConfiguration, parentDataType } = page;

        configuration = generateAlgoliaConfiguration({
            filter: dataConfiguration,
            filterType: dataType,
            parentFilter: parentDataConfiguration,
            parentFilterType: parentDataType,
            productIds: productids ?? productIds,
            campaign,
        });
    }

    const protocol = req.headers.referer?.split('://')[0] ?? 'https';
    const serverUrl = `${protocol}://${req.headers.host}${req.url}`;

    // We will use the default Algolia client for our search page. The rest will use our proxied client.
    const clientToUse = page.type === 'p10SearchPage' ? algoliaClient : proxySearchClient;

    /**
     * <Virtual* /> components are used server-side for Algolia to include facets/filters
     * on the initial state that will be generated.
     */
    const serverState = await getServerState(
        <InstantSearchSSRProvider>
            <InstantSearch
                indexName={ALGOLIA_INDEX_MAIN}
                searchClient={clientToUse}
                routing={createInstantSearchRouting(serverUrl)}
                future={{ preserveSharedStateOnUnmount: true }}
            >
                <Configure clickAnalytics />

                <Index indexName={ALGOLIA_INDEX_MAIN} indexId={ALGOLIA_INDEX_ID_PRODUCT_LIST}>
                    <Configure {...configuration} getRankingInfo hitsPerPage={pageSize} />

                    <VirtualPagination />
                    <VirtualSearch />
                    <VirtualFacets />
                    <VirtualSortBy />
                    <VirtualRefinementList
                        attribute={Facets.ACTIVE_PROMO_TYPE}
                        escapeFacetValues={false}
                        limit={1000}
                    />
                </Index>
            </InstantSearch>
        </InstantSearchSSRProvider>,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        { renderToString },
    );

    // getServerState can include undefined values (e.g. state.userToken), which getServerSideProps cannot serialize.
    const serializableServerState: typeof serverState = JSON.parse(JSON.stringify(serverState));

    return { serverState: serializableServerState, serverUrl };
};
